import React, { useRef } from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Loader2, Upload, Camera, Shield, Store } from 'lucide-react';
import { ImageWithFallback } from '../ui/image-with-fallback';

export function RegisterIdVerificationStep({
  isMerchant,
  error,
  statusMsg,
  loading,
  idFile,
  idPreview,
  setIdFile,
  setIdPreview,
  selfieFile,
  selfiePreview,
  setSelfieFile,
  setSelfiePreview,
  permitFile,
  permitPreview,
  setPermitFile,
  setPermitPreview,
  onBack,
  onSubmit,
}) {
  const idInputRef = useRef(null);
  const selfieInputRef = useRef(null);
  const permitInputRef = useRef(null);

  const handleIdFile = (e) => {
    const file = e.target.files[0];
    if (file) { setIdFile(file); setIdPreview(URL.createObjectURL(file)); }
  };
  const handleSelfieFile = (e) => {
    const file = e.target.files[0];
    if (file) { setSelfieFile(file); setSelfiePreview(URL.createObjectURL(file)); }
  };
  const handlePermitFile = (e) => {
    const file = e.target.files[0];
    if (file) { setPermitFile(file); setPermitPreview(URL.createObjectURL(file)); }
  };

  return (
    <div className="min-h-screen bg-[#000B18] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Shield className="w-12 h-12 text-[#00E5FF] mx-auto mb-4" />
          <h1 className="font-outfit text-3xl font-bold text-white mb-2">Verify Your Identity</h1>
          <p className="text-[#A0B4CC]">Upload a valid government ID and a selfie photo</p>
        </div>

        <Card className="bg-[#041A3B] border-white/10">
          <CardContent className="p-6 space-y-6">
            {error && (
              <div className="p-3 rounded-md bg-[#FF1744]/10 border border-[#FF1744]/30 text-[#FF1744] text-sm" data-testid="register-error">
                {error}
              </div>
            )}

            {loading && statusMsg && (
              <div
                className="p-3 rounded-md bg-[#00E5FF]/10 border border-[#00E5FF]/30 text-[#00E5FF] text-sm flex items-center gap-2"
                data-testid="register-status"
              >
                <Loader2 className="w-4 h-4 animate-spin" />
                {statusMsg}
              </div>
            )}

            {/* ID Upload */}
            <div>
              <Label className="text-white mb-2 block">Valid Government ID *</Label>
              <input type="file" ref={idInputRef} accept="image/*" onChange={handleIdFile} className="hidden" />
              {idPreview ? (
                <div className="relative rounded-lg overflow-hidden border border-white/20">
                  <ImageWithFallback
                    src={idPreview}
                    alt="ID Preview"
                    className="w-full h-40 object-cover"
                    fallback={
                      <div className="w-full h-40 bg-[#000B18] flex flex-col items-center justify-center gap-1 text-[#5C7599]">
                        <Upload className="w-7 h-7" />
                        <span className="text-xs">Preview unavailable — please re-upload</span>
                      </div>
                    }
                  />
                  <button onClick={() => { setIdFile(null); setIdPreview(null); }}
                    className="absolute top-2 right-2 bg-[#FF1744] text-white rounded-full w-6 h-6 flex items-center justify-center text-xs">X</button>
                </div>
              ) : (
                <button onClick={() => idInputRef.current?.click()} data-testid="upload-id-btn"
                  className="w-full h-32 rounded-lg border-2 border-dashed border-white/20 hover:border-[#00E5FF]/50 transition-colors flex flex-col items-center justify-center gap-2">
                  <Upload className="w-8 h-8 text-[#5C7599]" />
                  <span className="text-[#A0B4CC] text-sm">Upload your valid ID</span>
                  <span className="text-[#5C7599] text-xs">Name on ID must match your registration name</span>
                </button>
              )}
            </div>

            {/* Selfie Upload */}
            <div>
              <Label className="text-white mb-2 block">Selfie Photo *</Label>
              <input type="file" ref={selfieInputRef} accept="image/*" capture="user" onChange={handleSelfieFile} className="hidden" />
              {selfiePreview ? (
                <div className="relative rounded-lg overflow-hidden border border-white/20">
                  <ImageWithFallback
                    src={selfiePreview}
                    alt="Selfie Preview"
                    className="w-full h-40 object-cover"
                    fallback={
                      <div className="w-full h-40 bg-[#000B18] flex flex-col items-center justify-center gap-1 text-[#5C7599]">
                        <Camera className="w-7 h-7" />
                        <span className="text-xs">Preview unavailable — please re-upload</span>
                      </div>
                    }
                  />
                  <button onClick={() => { setSelfieFile(null); setSelfiePreview(null); }}
                    className="absolute top-2 right-2 bg-[#FF1744] text-white rounded-full w-6 h-6 flex items-center justify-center text-xs">X</button>
                </div>
              ) : (
                <button onClick={() => selfieInputRef.current?.click()} data-testid="upload-selfie-btn"
                  className="w-full h-32 rounded-lg border-2 border-dashed border-white/20 hover:border-[#00E5FF]/50 transition-colors flex flex-col items-center justify-center gap-2">
                  <Camera className="w-8 h-8 text-[#5C7599]" />
                  <span className="text-[#A0B4CC] text-sm">Take or upload a selfie</span>
                  <span className="text-[#5C7599] text-xs">Face must be clearly visible</span>
                </button>
              )}
            </div>

            {/* Business Permit Upload (Merchant only) */}
            {isMerchant && (
              <div>
                <Label className="text-white mb-2 block">Business Permit / Document (Optional)</Label>
                <input type="file" ref={permitInputRef} accept="image/*,.pdf" onChange={handlePermitFile} className="hidden" />
                {permitPreview ? (
                  <div className="relative rounded-lg overflow-hidden border border-[#00E676]/30">
                    <ImageWithFallback
                      src={permitPreview}
                      alt="Permit Preview"
                      className="w-full h-32 object-cover"
                      fallback={
                        <div className="w-full h-32 bg-[#000B18] flex flex-col items-center justify-center gap-1 text-[#5C7599]">
                          <Store className="w-6 h-6" />
                          <span className="text-xs">Preview unavailable — please re-upload</span>
                        </div>
                      }
                    />
                    <button onClick={() => { setPermitFile(null); setPermitPreview(null); }}
                      className="absolute top-2 right-2 bg-[#FF1744] text-white rounded-full w-6 h-6 flex items-center justify-center text-xs">X</button>
                  </div>
                ) : (
                  <button onClick={() => permitInputRef.current?.click()} data-testid="upload-permit-btn"
                    className="w-full h-24 rounded-lg border-2 border-dashed border-[#00E676]/30 hover:border-[#00E676]/60 transition-colors flex flex-col items-center justify-center gap-1.5 bg-[#00E676]/5">
                    <Store className="w-6 h-6 text-[#00E676]/60" />
                    <span className="text-[#A0B4CC] text-xs">Upload business permit or registration</span>
                  </button>
                )}
              </div>
            )}

            <div className="flex items-center gap-2">
              <div className="h-1 flex-1 rounded-full bg-[#00E5FF]"></div>
              <div className="h-1 flex-1 rounded-full bg-[#00E5FF]"></div>
              <span className="text-[#5C7599] text-xs">Step 2 of 2</span>
            </div>

            <div className="flex gap-3">
              <Button onClick={onBack} variant="ghost" className="text-[#A0B4CC]" data-testid="back-step1-btn">
                Back
              </Button>
              <Button onClick={onSubmit} disabled={loading || !idFile || !selfieFile}
                className="flex-1 btn-primary h-11" data-testid="register-final-btn">
                {loading ? (
                  <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Verifying & Creating Account...</>
                ) : (
                  <><Shield className="w-4 h-4 mr-2" /> Create Account</>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
